// Adapted from https://github.com/visgl/react-google-maps/blob/main/examples/autocomplete/src/hooks/use-autocomplete-suggestions.ts

import { useEffect, useRef, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';

import { envInfo } from '@constants/constants';

import {
  ADDRESS_INCLUDED_PRIMARY_TYPES,
  ADDRESS_INCLUDED_REGION_CODES,
  DEBOUNCE_MS,
  GOOGLE_MAPS_LIBRARIES,
} from './mapOptions';

export interface AutocompleteSuggestionItem {
  placePrediction: google.maps.places.PlacePrediction;
  text: string;
}

export interface UseAutocompleteSuggestionsResult {
  isLoaded: boolean;
  isLoading: boolean;
  suggestions: AutocompleteSuggestionItem[];
  resetSession: () => void;
}

export function useAutocompleteSuggestions(inputString: string): UseAutocompleteSuggestionsResult {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: envInfo.googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const places = window.google?.maps?.places;
    if (!isLoaded || !places?.AutocompleteSuggestion || !places?.AutocompleteSessionToken) {
      return;
    }
    const SuggestionCls = places.AutocompleteSuggestion;
    const SessionTokenCls = places.AutocompleteSessionToken;

    const query = inputString.trim();
    if (!query) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    let disposed = false;
    const debounceTimer = setTimeout(async () => {
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new SessionTokenCls();
      }
      const request: google.maps.places.AutocompleteRequest = {
        input: query,
        sessionToken: sessionTokenRef.current,
        includedPrimaryTypes: [...ADDRESS_INCLUDED_PRIMARY_TYPES],
        includedRegionCodes: [...ADDRESS_INCLUDED_REGION_CODES],
      };

      setIsLoading(true);
      try {
        const { suggestions: results } = await SuggestionCls.fetchAutocompleteSuggestions(request);
        if (disposed) return;
        const mapped: AutocompleteSuggestionItem[] = results
          .filter(s => s.placePrediction != null)
          .map(s => ({
            placePrediction: s.placePrediction as google.maps.places.PlacePrediction,
            text: s.placePrediction?.text?.text ?? '',
          }));
        setSuggestions(mapped);
      } catch (err) {
        if (disposed) return;

        console.warn('[useAutocompleteSuggestions] failed to fetch suggestions', err);
        setSuggestions([]);
      } finally {
        if (!disposed) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      disposed = true;
      clearTimeout(debounceTimer);
    };
  }, [inputString, isLoaded]);

  return {
    isLoaded,
    isLoading,
    suggestions,
    resetSession: () => {
      sessionTokenRef.current = null;
      setSuggestions([]);
    },
  };
}
